import React, { useState, useRef } from 'react';
import { Input, Label, Button } from "../components/ui";
import { optionsPartido } from '../datos/options';
import { EmailVerification } from '../components/EmailVerification';
import { jsPDF } from 'jspdf';

export const UserRequestPage = () => {
    const [loading, setLoading] = useState(false);
    const [pdfUploading, setPdfUploading] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState(null);
    const [emailVerified, setEmailVerified] = useState(false);
    const [verifiedEmail, setVerifiedEmail] = useState('');
    const [formData, setFormData] = useState({
        codigo_partido: '',
        nombre_municipio: '',
        nombre_intendente: '',
        nombre_responsable: '',
        email_responsable: '',
        nombre_invitado: '',
        email_invitado: '',
        organizacion: '',
        solicited_role: 'editor_municipio',
    });

    const isInvitado = formData.solicited_role === 'visualizador_municipio';
    const [pdfFile, setPdfFile] = useState(null);
    const fileInputRef = useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // If municipality changes, update nombre_municipio automatically
        if (name === 'codigo_partido') {
            const selectedOption = optionsPartido.find(opt => opt.value === value);
            setFormData(prev => ({
                ...prev,
                [name]: value,
                nombre_municipio: selectedOption ? selectedOption.label : ''
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Función para convertir imagen a PDF
    const convertImageToPdf = async (imageFile) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    // Crear PDF con dimensiones A4
                    const pdf = new jsPDF('p', 'mm', 'a4');
                    const pageWidth = 210;
                    const pageHeight = 297;
                    const margin = 10;

                    // Calcular dimensiones para que la imagen quepa en la página
                    const availableWidth = pageWidth - 2 * margin;
                    const availableHeight = pageHeight - 2 * margin;
                    
                    let imgWidth = img.width;
                    let imgHeight = img.height;
                    const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
                    
                    imgWidth *= ratio;
                    imgHeight *= ratio;

                    // Centrar la imagen
                    const x = (pageWidth - imgWidth) / 2;
                    const y = (pageHeight - imgHeight) / 2;

                    // Agregar imagen al PDF
                    pdf.addImage(event.target.result, 'JPEG', x, y, imgWidth, imgHeight);
                    
                    // Generar PDF como Blob
                    const pdfBlob = pdf.output('blob');
                    
                    // Crear nuevo archivo con nombre original pero extensión PDF
                    const pdfFile = new File([pdfBlob], imageFile.name.replace(/\.(jpg|jpeg|png)$/i, '.pdf'), {
                        type: 'application/pdf'
                    });
                    
                    resolve(pdfFile);
                };
                img.onerror = () => reject(new Error('Error al cargar la imagen'));
                img.src = event.target.result;
            };
            reader.onerror = () => reject(new Error('Error al leer el archivo'));
            reader.readAsDataURL(imageFile);
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        
        if (!file) {
            setPdfFile(null);
            return;
        }

        // Detectar tipo de archivo
        const isPdf = file.type === 'application/pdf';
        const isImage = file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png';

        if (!isPdf && !isImage) {
            setMessage('Por favor seleccione un archivo PDF o imagen (JPEG, PNG)');
            setMessageType('error');
            setPdfFile(null);
            return;
        }

        setPdfUploading(true);
        
        try {
            let finalFile = file;
            
            // Si es imagen, convertir a PDF
            if (isImage) {
                finalFile = await convertImageToPdf(file);
            }
            
            setPdfFile(finalFile);
        } catch (error) {
            console.error('Error procesando archivo:', error);
            setMessage('Error al procesar el archivo. Intente nuevamente.');
            setMessageType('error');
            setPdfFile(null);
        } finally {
            setPdfUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!pdfFile) {
            setMessage('Debe adjuntar la nota firmada en formato PDF');
            setMessageType('error');
            return;
        }

        setLoading(true);
        setMessage(null);

        try {
            // Create FormData for file upload
            const submitData = new FormData();
            submitData.append('codigo_partido', formData.codigo_partido);
            submitData.append('nombre_municipio', formData.nombre_municipio);
            submitData.append('solicited_role', formData.solicited_role);

            if (isInvitado) {
                submitData.append('nombre_responsable', formData.nombre_invitado);
                submitData.append('email_responsable', formData.email_invitado);
                submitData.append('nombre_intendente', '');
                submitData.append('organizacion', formData.organizacion);
            } else {
                submitData.append('nombre_intendente', formData.nombre_intendente);
                submitData.append('nombre_responsable', formData.nombre_responsable);
                submitData.append('email_responsable', formData.email_responsable);
            }
            submitData.append('nota_pdf', pdfFile);

            const response = await fetch('/api/user-requests/request', {
                method: 'POST',
                body: submitData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al enviar la solicitud');
            }

            setMessage('Solicitud enviada correctamente. Será notificado por email cuando sea aprobada.');
            setMessageType('success');
            
            // Reset form
            setFormData({
                codigo_partido: '',
                nombre_municipio: '',
                nombre_intendente: '',
                nombre_responsable: '',
                email_responsable: '',
                nombre_invitado: '',
                email_invitado: '',
                organizacion: '',
                solicited_role: 'editor_municipio',
            });
            setPdfFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            setMessage(error.message);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleEmailVerified = (email) => {
        setEmailVerified(true);
        setVerifiedEmail(email);
        setFormData(prev => ({
            ...prev,
            email_contacto: email
        }));
    };

    return (
        <div className="min-h-screen flex flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
            <div className="sm:mx-auto sm:w-full sm:max-w-lg">
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Solicitud de acceso - Municipios
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    Seleccione el tipo de acceso que desea solicitar.
                </p>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    RED - Registro de Entidades Deportivas
                </p>
            </div>

            {/* Nota modelo para descargar */}
            <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-lg">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                    <div className="flex items-start gap-3">
                        <svg className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <h4 className="text-sm font-medium text-amber-800">Nota Modelo Obligatoria</h4>
                            <p className="text-sm text-amber-700 mt-1">
                                Descargue la nota modelo según el tipo de acceso, complete los datos solicitados, fírmela y adjúntela en formato PDF.
                            </p>
                            <a 
                                href={isInvitado ? "/Nota_Solicitud_Visualizador_Provincial.pdf" : "/Nota_Solicitud_Municipio.pdf"} 
                                download
                                className="inline-flex items-center gap-2 mt-2 text-sm font-medium text-amber-700 hover:text-amber-900 underline"
                            >
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                Descargar Nota Modelo ({isInvitado ? 'Visualización' : 'Edición'})
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
                <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    
                    {message && messageType === "success" && (
                        <div className="mb-4 rounded-md bg-green-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-green-800">{message}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {message && messageType === "error" && (
                        <div className="mb-4 rounded-md bg-red-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-red-800">{message}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Email Verification Step */}
                    {!emailVerified ? (
                        <div className="p-6">
                            <EmailVerification onVerified={handleEmailVerified} />
                        </div>
                    ) : (
                    <form className="space-y-6" onSubmit={handleSubmit} encType="multipart/form-data">
                        {/* Municipio */}
                        <div>
                            <Label htmlFor="codigo_partido">Municipio <span className="text-red-500">*</span></Label>
                            <select
                                id="codigo_partido"
                                name="codigo_partido"
                                value={formData.codigo_partido}
                                onChange={handleChange}
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                            >
                                <option value="">Seleccione un municipio...</option>
                                {optionsPartido.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {!isInvitado && (
                            <>
                                <div>
                                    <Label htmlFor="nombre_intendente">Nombre del Intendente/a <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="nombre_intendente"
                                        name="nombre_intendente"
                                        type="text"
                                        placeholder="Nombre completo del Intendente/a"
                                        value={formData.nombre_intendente}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="nombre_responsable">Nombre del Responsable de Edición <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="nombre_responsable"
                                        name="nombre_responsable"
                                        type="text"
                                        placeholder="Nombre completo del responsable"
                                        value={formData.nombre_responsable}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="email_responsable">Email del Responsable <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="email_responsable"
                                        name="email_responsable"
                                        type="email"
                                        placeholder="correo@ejemplo.com"
                                        value={formData.email_responsable}
                                        onChange={handleChange}
                                        required
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Este email será usado para el registro y notificaciones.</p>
                                </div>
                            </>
                        )}

                        {isInvitado && (
                            <>
                                <div>
                                    <Label htmlFor="nombre_invitado">Nombre del Invitado <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="nombre_invitado"
                                        name="nombre_invitado"
                                        type="text"
                                        placeholder="Nombre completo del invitado"
                                        value={formData.nombre_invitado}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="organizacion">Organización que representa <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="organizacion"
                                        name="organizacion"
                                        type="text"
                                        placeholder="Ej: Club Atlético, Liga Deportiva, etc."
                                        value={formData.organizacion}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="email_invitado">Email del Invitado <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="email_invitado"
                                        name="email_invitado"
                                        type="email"
                                        placeholder="correo@ejemplo.com"
                                        value={formData.email_invitado}
                                        onChange={handleChange}
                                        required
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Este email será usado para el registro y notificaciones del invitado.</p>
                                </div>
                            </>
                        )}

                        {/* Tipo de acceso */}
                        <div>
                            <Label>Tipo de acceso <span className="text-red-500">*</span></Label>
                            <div className="mt-2 space-y-3">
                                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <input
                                        type="radio"
                                        name="solicited_role"
                                        value="editor_municipio"
                                        checked={formData.solicited_role === 'editor_municipio'}
                                        onChange={handleChange}
                                        className="mt-1 h-4 w-4 text-blue-600 border-gray-300"
                                    />
                                    <div>
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">Editor municipal</div>
                                        <div className="text-xs text-gray-500">Puede ver, crear y modificar entidades del municipio</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <input
                                        type="radio"
                                        name="solicited_role"
                                        value="visualizador_municipio"
                                        checked={formData.solicited_role === 'visualizador_municipio'}
                                        onChange={handleChange}
                                        className="mt-1 h-4 w-4 text-blue-600 border-gray-300"
                                    />
                                    <div>
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">Invitado</div>
                                        <div className="text-xs text-gray-500">Solo puede ver la información cargada, sin posibilidad de editar</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Nota PDF */}
                        <div>
                            <Label htmlFor="nota_pdf">Nota de Solicitud (PDF firmado por el Intendete/a) <span className="text-red-500">*</span></Label>
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                                <div className="space-y-1 text-center">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <div className="flex text-sm text-gray-600 justify-center">
                                        <label htmlFor="nota_pdf" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                            <span>Subir archivo</span>
                                            <input
                                                id="nota_pdf"
                                                name="nota_pdf"
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={handleFileChange}
                                                ref={fileInputRef}
                                                className="sr-only"
                                                required
                                                disabled={pdfUploading}
                                            />
                                        </label>
                                    </div>
                                    <p className="text-xs text-gray-500">PDF o imagen (JPEG, PNG) hasta 10MB</p>
                                    {pdfUploading && (
                                        <p className="text-sm text-blue-600 font-medium flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Procesando archivo...
                                        </p>
                                    )}
                                    {pdfFile && !pdfUploading && (
                                        <p className="text-sm text-green-600 font-medium">
                                            Archivo seleccionado: {pdfFile.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                {isInvitado
                                    ? 'Adjunte la nota solicitando autorización para la visualización por parte de la organización.'
                                    : 'Adjunte la nota firmada por el Intendente solicitando acceso al sistema.'}
                            </p>
                        </div>

                        <div>
                            <Button 
                                variant="subse"
                                type="submit" 
                                disabled={loading || !formData.codigo_partido || !pdfFile || pdfUploading || (isInvitado ? (!formData.nombre_invitado || !formData.organizacion || !formData.email_invitado) : (!formData.nombre_intendente || !formData.nombre_responsable || !formData.email_responsable))}
                            >
                                Enviar Solicitud
                            </Button>
                        </div>
                    </form>
                    )}

                    <div className="mt-6 border-t border-gray-200 pt-6">
                        <p className="text-xs text-gray-500 text-center">
                            ¿Ya tiene una solicitud aprobada?{' '}
                            <a href="/register" className="text-blue-600 hover:text-blue-500 font-medium">
                                Complete su registro aquí
                            </a>
                        </p>
                    </div>
                    
                </div>
            </div>
        </div>
    );
};

export default UserRequestPage;
